import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	type SensorDescriptor,
	type SensorOptions,
} from '@dnd-kit/core';
import {
	restrictToParentElement,
	restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
	SortableContext,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box, Group, Stack } from '@mantine/core';
import type { EmbeddedPromptParentOut } from '@repo/api-codegen';
import type { IconNames } from '@repo/foundations';
import { Icon, Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { ReorderableChatHistoryItem } from './ReorderableChatHistoryItem';

interface ReorderableChatHistoryGroupProps {
	chats: EmbeddedPromptParentOut[];
	onSelectChat: (chat: EmbeddedPromptParentOut) => void;
	onDeleteChat: (chat: EmbeddedPromptParentOut) => void;
	groupName: string;
	groupIconName?: IconNames;
	editingChatId: string | null;
	editingChatTitle: string;
	onStartEditingChatTitle: (chatId: string, currentTitle: string) => void;
	onStopEditingChatTitle: () => void;
	onSaveEditingChatTitle: (chatId: string) => void;
	onModifyEditingChatTitle: (title: string) => void;
	onDragEnd: (event: DragEndEvent) => void;
	onTogglePin: (chatId: string) => void;
	activeChatId: string;
	sensors: SensorDescriptor<SensorOptions>[];
}

export const ReorderableChatHistoryGroup = observer(
	({
		chats,
		onSelectChat,
		onDeleteChat,
		groupName,
		groupIconName,
		editingChatId,
		editingChatTitle,
		onStartEditingChatTitle,
		onStopEditingChatTitle,
		onSaveEditingChatTitle,
		onModifyEditingChatTitle,
		onDragEnd,
		onTogglePin,
		activeChatId,
		sensors,
	}: ReorderableChatHistoryGroupProps) => {
		if (chats.length === 0) {
			return null;
		}

		return (
			<Box>
				<Stack spacing="5xs">
					<Group spacing="4xs">
						{groupIconName && (
							<Box px="4xs">
								<Icon
									name={groupIconName}
									size="md"
									color="icon/secondary/default"
									fillColor="icon/secondary/default"
									iconPadding={0}
								/>
							</Box>
						)}
						<Text
							size="xs"
							color="text/secondary/default"
							fw={600}
							px={!groupIconName ? 'xs' : undefined}
							py="3xs"
						>
							{groupName}
						</Text>
					</Group>
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={onDragEnd}
						modifiers={[restrictToParentElement, restrictToVerticalAxis]}
					>
						<SortableContext
							items={chats.map((chat: EmbeddedPromptParentOut) => chat.id)}
							strategy={verticalListSortingStrategy}
						>
							{chats.map((chat: EmbeddedPromptParentOut) => (
								<ReorderableChatHistoryItem
									key={chat.id}
									chat={chat}
									isActive={chat.id === activeChatId}
									isPinned={true}
									onSelectChat={onSelectChat}
									onDeleteChat={onDeleteChat}
									editingChatId={editingChatId}
									editingChatTitle={editingChatTitle}
									onStartEditingChatTitle={onStartEditingChatTitle}
									onStopEditingChatTitle={onStopEditingChatTitle}
									onSaveEditingChatTitle={onSaveEditingChatTitle}
									onModifyEditingChatTitle={onModifyEditingChatTitle}
									onTogglePin={onTogglePin}
								/>
							))}
						</SortableContext>
					</DndContext>
				</Stack>
			</Box>
		);
	}
);
